/**
 * useTaskSubscription Hook
 *
 * Subscribes to real-time Task WebSocket events and updates React Query cache.
 * Automatically subscribes when a project is active and unsubscribes on cleanup.
 *
 * @module hooks/useTaskSubscription
 */

import { useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import useWebSocket from './useWebSocket.js';
import { taskKeys } from './useTasks.js';

// ============================================
// Constants
// ============================================

/**
 * Task-related WebSocket message types
 */
const TASK_WS_TYPES = {
  // Outgoing (client -> server)
  SUBSCRIBE: 'subscribe_tasks',
  UNSUBSCRIBE: 'unsubscribe_tasks',

  // Incoming (server -> client)
  CREATED: 'task_created',
  UPDATED: 'task_updated',
  DELETED: 'task_deleted',
  STATUS_CHANGED: 'task_status_changed',
  ASSIGNED: 'task_assigned',
  UNASSIGNED: 'task_unassigned',
  COMMENT_ADDED: 'task_comment_added'
};

// ============================================
// Hook
// ============================================

/**
 * useTaskSubscription Hook
 *
 * Subscribes to real-time task updates for a specific project.
 * Updates React Query cache when task events are received.
 *
 * @param {string} projectId - Project ID to subscribe to (null to disable)
 * @param {object} [options] - Hook options
 * @param {boolean} [options.enabled=true] - Whether subscription is enabled
 * @param {function} [options.onTaskCreated] - Callback when task is created
 * @param {function} [options.onTaskUpdated] - Callback when task is updated
 * @param {function} [options.onTaskDeleted] - Callback when task is deleted
 * @param {function} [options.onTaskStatusChanged] - Callback when status changes
 * @param {function} [options.onTaskAssigned] - Callback when task is assigned
 * @param {function} [options.onTaskUnassigned] - Callback when task is unassigned
 * @param {function} [options.onTaskCommentAdded] - Callback when comment is added
 * @returns {object} Subscription state
 */
export function useTaskSubscription(projectId, options = {}) {
  const {
    enabled = true,
    onTaskCreated,
    onTaskUpdated,
    onTaskDeleted,
    onTaskStatusChanged,
    onTaskAssigned,
    onTaskUnassigned,
    onTaskCommentAdded
  } = options;

  const queryClient = useQueryClient();
  const { send, subscribe, isConnected } = useWebSocket();
  const subscribedProjectRef = useRef(null);

  // Debug: Log render with current isConnected value
  console.log('[TaskSubscription] RENDER - isConnected:', isConnected, 'projectId:', projectId);

  // ========================================
  // Event Handlers
  // ========================================

  /**
   * Handle task_created event
   */
  const handleTaskCreated = useCallback((data) => {
    const { task } = data;

    // Invalidate task lists to include new task
    queryClient.invalidateQueries({ queryKey: taskKeys.lists() });
    queryClient.invalidateQueries({ queryKey: taskKeys.myTasks() });

    // Call user callback
    onTaskCreated?.(task, data.createdBy);
  }, [queryClient, onTaskCreated]);

  /**
   * Handle task_updated event
   */
  const handleTaskUpdated = useCallback((data) => {
    const { taskId, changes } = data;

    // Update the specific task in cache if it exists
    queryClient.setQueryData(taskKeys.detail(taskId), (oldData) => {
      if (!oldData) return oldData;
      return { ...oldData, ...changes };
    });

    // Invalidate lists in case sorting changed
    queryClient.invalidateQueries({ queryKey: taskKeys.lists() });

    // Call user callback
    onTaskUpdated?.(taskId, changes, data.updatedBy);
  }, [queryClient, onTaskUpdated]);

  /**
   * Handle task_deleted event
   */
  const handleTaskDeleted = useCallback((data) => {
    const { taskId } = data;

    // Remove from cache
    queryClient.removeQueries({ queryKey: taskKeys.detail(taskId) });

    // Invalidate lists
    queryClient.invalidateQueries({ queryKey: taskKeys.lists() });
    queryClient.invalidateQueries({ queryKey: taskKeys.myTasks() });

    // Call user callback
    onTaskDeleted?.(taskId, data.deletedBy);
  }, [queryClient, onTaskDeleted]);

  /**
   * Handle task_status_changed event
   */
  const handleTaskStatusChanged = useCallback((data) => {
    const { taskId, previousStatus, newStatus } = data;

    // Optimistically update the task status in cache
    queryClient.setQueryData(taskKeys.detail(taskId), (oldData) => {
      if (!oldData) return oldData;
      return { ...oldData, status: newStatus };
    });

    // Invalidate lists since status affects column placement
    queryClient.invalidateQueries({ queryKey: taskKeys.lists() });
    queryClient.invalidateQueries({ queryKey: taskKeys.myTasks() });

    // Invalidate history for this task
    queryClient.invalidateQueries({ queryKey: taskKeys.history(taskId) });

    // Call user callback
    onTaskStatusChanged?.(taskId, previousStatus, newStatus, data.changedBy, data.reason);
  }, [queryClient, onTaskStatusChanged]);

  /**
   * Handle task_assigned event
   */
  const handleTaskAssigned = useCallback((data) => {
    const { taskId, assignee } = data;

    // Update the task assignee in cache
    queryClient.setQueryData(taskKeys.detail(taskId), (oldData) => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        assignee,
        assigneeId: assignee.id,
        assigneeType: assignee.type,
        assignedAt: new Date().toISOString()
      };
    });

    // Invalidate my tasks if current user is involved
    queryClient.invalidateQueries({ queryKey: taskKeys.myTasks() });

    // Invalidate history
    queryClient.invalidateQueries({ queryKey: taskKeys.history(taskId) });

    // Call user callback
    onTaskAssigned?.(taskId, assignee, data.assignedBy);
  }, [queryClient, onTaskAssigned]);

  /**
   * Handle task_unassigned event
   */
  const handleTaskUnassigned = useCallback((data) => {
    const { taskId } = data;

    // Clear the task assignee in cache
    queryClient.setQueryData(taskKeys.detail(taskId), (oldData) => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        assignee: null,
        assigneeId: null,
        assigneeType: null,
        assignedAt: null
      };
    });

    // Invalidate my tasks
    queryClient.invalidateQueries({ queryKey: taskKeys.myTasks() });

    // Invalidate history
    queryClient.invalidateQueries({ queryKey: taskKeys.history(taskId) });

    // Call user callback
    onTaskUnassigned?.(taskId, data.previousAssignee, data.unassignedBy);
  }, [queryClient, onTaskUnassigned]);

  /**
   * Handle task_comment_added event
   */
  const handleTaskCommentAdded = useCallback((data) => {
    const { taskId, comment } = data;

    // Invalidate comments for this task
    queryClient.invalidateQueries({ queryKey: taskKeys.comments(taskId) });

    // Update comment count in task detail
    queryClient.setQueryData(taskKeys.detail(taskId), (oldData) => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        _count: {
          ...oldData._count,
          comments: (oldData._count?.comments || 0) + 1
        }
      };
    });

    // Call user callback
    onTaskCommentAdded?.(taskId, comment, data.author);
  }, [queryClient, onTaskCommentAdded]);

  // ========================================
  // Subscription Management
  // ========================================

  /**
   * Subscribe to task events for a project
   */
  const subscribeToProject = useCallback((projId) => {
    if (!projId || !isConnected) {
      console.log('[TaskSubscription] Cannot subscribe - projId:', projId, 'isConnected:', isConnected);
      return;
    }

    console.log('[TaskSubscription] Subscribing to project:', projId);
    try {
      send({
        type: TASK_WS_TYPES.SUBSCRIBE,
        projectId: projId
      });
      subscribedProjectRef.current = projId;
      console.log('[TaskSubscription] Subscription sent for project:', projId);
    } catch (err) {
      console.error('[TaskSubscription] Failed to send subscribe:', err);
    }
  }, [send, isConnected]);

  /**
   * Unsubscribe from task events
   */
  const unsubscribeFromProject = useCallback((projId) => {
    if (!projId || !isConnected) return;

    send({
      type: TASK_WS_TYPES.UNSUBSCRIBE,
      projectId: projId
    });

    if (subscribedProjectRef.current === projId) {
      subscribedProjectRef.current = null;
    }
  }, [send, isConnected]);

  // ========================================
  // Effects
  // ========================================

  // Register message handlers
  useEffect(() => {
    if (!enabled) return;
    // Guard: ensure subscribe is available (WebSocket may not be initialized yet)
    if (typeof subscribe !== 'function') return;

    const cleanups = [
      subscribe(TASK_WS_TYPES.CREATED, handleTaskCreated),
      subscribe(TASK_WS_TYPES.UPDATED, handleTaskUpdated),
      subscribe(TASK_WS_TYPES.DELETED, handleTaskDeleted),
      subscribe(TASK_WS_TYPES.STATUS_CHANGED, handleTaskStatusChanged),
      subscribe(TASK_WS_TYPES.ASSIGNED, handleTaskAssigned),
      subscribe(TASK_WS_TYPES.UNASSIGNED, handleTaskUnassigned),
      subscribe(TASK_WS_TYPES.COMMENT_ADDED, handleTaskCommentAdded)
    ];

    return () => {
      cleanups.forEach(cleanup => cleanup?.());
    };
  }, [
    enabled,
    subscribe,
    handleTaskCreated,
    handleTaskUpdated,
    handleTaskDeleted,
    handleTaskStatusChanged,
    handleTaskAssigned,
    handleTaskUnassigned,
    handleTaskCommentAdded
  ]);

  // Manage subscription based on projectId
  // Note: We subscribe directly here instead of calling subscribeToProject
  // to avoid stale closure issues with isConnected
  useEffect(() => {
    console.log('[TaskSubscription] Effect triggered - enabled:', enabled, 'isConnected:', isConnected, 'projectId:', projectId);

    if (!enabled || !isConnected) {
      console.log('[TaskSubscription] Skipping - enabled:', enabled, 'isConnected:', isConnected);
      return;
    }

    const currentSubscription = subscribedProjectRef.current;

    // Unsubscribe from previous project if different
    if (currentSubscription && currentSubscription !== projectId) {
      console.log('[TaskSubscription] Unsubscribing from previous project:', currentSubscription);
      try {
        send({
          type: TASK_WS_TYPES.UNSUBSCRIBE,
          projectId: currentSubscription
        });
      } catch (err) {
        console.error('[TaskSubscription] Failed to unsubscribe:', err);
      }
      subscribedProjectRef.current = null;
    }

    // Subscribe to new project - directly here to avoid stale closure
    if (projectId && projectId !== subscribedProjectRef.current) {
      console.log('[TaskSubscription] Subscribing to project:', projectId);
      try {
        send({
          type: TASK_WS_TYPES.SUBSCRIBE,
          projectId: projectId
        });
        subscribedProjectRef.current = projectId;
        console.log('[TaskSubscription] Subscription sent for project:', projectId);
      } catch (err) {
        console.error('[TaskSubscription] Failed to send subscribe:', err);
      }
    }

    // Cleanup on unmount
    return () => {
      if (subscribedProjectRef.current && isConnected) {
        console.log('[TaskSubscription] Cleanup - unsubscribing from:', subscribedProjectRef.current);
        try {
          send({
            type: TASK_WS_TYPES.UNSUBSCRIBE,
            projectId: subscribedProjectRef.current
          });
        } catch (err) {
          // Ignore errors on cleanup
        }
        subscribedProjectRef.current = null;
      }
    };
  }, [projectId, enabled, isConnected, send]);

  // ========================================
  // Return Value
  // ========================================

  return {
    isSubscribed: !!subscribedProjectRef.current,
    subscribedProject: subscribedProjectRef.current,
    isConnected
  };
}

export default useTaskSubscription;
